import { useEffect, useMemo, useRef, useState } from 'react';
import { API_URL as API } from '../../config/env.js';
import { adminHeaders, adminAuthErrorMessage } from '../../utils/adminAuth.js';
import { BUSINESS } from '../../config/business.js';

function formatDate(ts) {
  if (!ts) return '—';
  const d = new Date(Number(ts));
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateTime(ts) {
  if (!ts) return '—';
  const d = new Date(Number(ts));
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatJoined(createdAt) {
  if (!createdAt) return '—';
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatMoney(paise) {
  return `₹${(Number(paise || 0) / 100).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const STATUS_LABEL = {
  paid: 'Paid',
  complimentary: 'Free unlock',
  trial: 'Trial',
  expired: 'Expired',
  free: 'Free',
};

function initials(name, email) {
  const src = String(name || email || '?').trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

function matchesSearch(u, q) {
  if (!q) return true;
  const hay = `${u.name || ''} ${u.email || ''} ${u.phone || ''}`.toLowerCase();
  return hay.includes(q);
}

export default function AdminSubscribers({ token, onLogout, showToast }) {
  const [view, setView] = useState('readers'); // readers | bills
  const [data, setData] = useState({ stats: null, users: [] });
  const [invoiceData, setInvoiceData] = useState({ stats: null, invoices: [], business: null });
  const [loading, setLoading] = useState(true);
  const [billsLoading, setBillsLoading] = useState(false);
  const [tab, setTab] = useState('paid');
  const [search, setSearch] = useState('');
  const [billSearch, setBillSearch] = useState('');
  const [editPhone, setEditPhone] = useState({});
  const [editingPhoneId, setEditingPhoneId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [whatsappConfigured, setWhatsappConfigured] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [syncingInvoices, setSyncingInvoices] = useState(false);
  const [grantingId, setGrantingId] = useState(null);
  const [revokingId, setRevokingId] = useState(null);
  const [grantEmail, setGrantEmail] = useState('');
  const [grantingEmail, setGrantingEmail] = useState(false);
  const [recoverPaymentId, setRecoverPaymentId] = useState('');
  const [recoveringPayment, setRecoveringPayment] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [invoiceModal, setInvoiceModal] = useState(null); // { id, invoiceNo, html, loading }
  const invoiceFrameRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/subscribers`, { headers: adminHeaders(token) });
      const json = await res.json().catch(() => ({}));
      if (res.status === 401) return onLogout(adminAuthErrorMessage(res, json));
      if (!res.ok) return showToast(json.error || 'Could not load subscribers', 'error');
      setData({ stats: json.stats, users: json.users || [] });
      setWhatsappConfigured(!!json.whatsappConfigured);
      const phones = {};
      (json.users || []).forEach((u) => {
        phones[u.id] = {
          phone: u.phone || '',
          optIn: u.whatsapp_opt_in !== false && u.whatsapp_opt_in !== 0,
        };
      });
      setEditPhone(phones);
      setEditingPhoneId(null);
    } catch {
      showToast('Network error loading subscribers', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadInvoices = async () => {
    setBillsLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/invoices`, { headers: adminHeaders(token) });
      const json = await res.json().catch(() => ({}));
      if (res.status === 401) return onLogout(adminAuthErrorMessage(res, json));
      if (!res.ok) return showToast(json.error || 'Could not load GST bills', 'error');
      setInvoiceData({
        stats: json.stats,
        invoices: json.invoices || [],
        business: json.business || null,
      });
    } catch {
      showToast('Network error loading GST bills', 'error');
    } finally {
      setBillsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [token]);

  useEffect(() => {
    if (view === 'bills') loadInvoices();
  }, [view, token]);

  const savePhone = async (userId) => {
    const row = editPhone[userId] || {};
    const digits = String(row.phone || '').replace(/\D/g, '');
    if (digits.length < 10) {
      showToast('Enter a valid 10-digit mobile number', 'error');
      return;
    }
    setSavingId(userId);
    try {
      const res = await fetch(`${API}/api/admin/subscribers/${userId}/phone`, {
        method: 'PATCH',
        headers: { ...adminHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: row.phone,
          whatsapp_opt_in: !!row.optIn,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 401) return onLogout(adminAuthErrorMessage(res, json));
      if (!res.ok) showToast(json.error || 'Could not save phone', 'error');
      else {
        showToast('Mobile / WhatsApp saved');
        setEditingPhoneId(null);
        load();
      }
    } catch {
      showToast('Network error saving phone', 'error');
    }
    setSavingId(null);
  };

  const startEditPhone = (u) => {
    setEditPhone((prev) => ({
      ...prev,
      [u.id]: {
        phone: prev[u.id]?.phone ?? u.phone ?? '',
        optIn: prev[u.id]?.optIn ?? (u.whatsapp_opt_in !== false && u.whatsapp_opt_in !== 0),
      },
    }));
    setEditingPhoneId(u.id);
  };

  const cancelEditPhone = (u) => {
    setEditPhone((prev) => ({
      ...prev,
      [u.id]: {
        phone: u.phone || '',
        optIn: u.whatsapp_opt_in !== false && u.whatsapp_opt_in !== 0,
      },
    }));
    setEditingPhoneId(null);
  };

  const formatMobileDisplay = (phone) => {
    const digits = String(phone || '').replace(/\D/g, '');
    if (!digits) return '';
    if (digits.length === 12 && digits.startsWith('91')) return `+91 ${digits.slice(2)}`;
    if (digits.length === 10) return `+91 ${digits}`;
    return phone;
  };

  const exportCsv = async () => {
    try {
      const res = await fetch(`${API}/api/admin/subscribers/export`, { headers: adminHeaders(token) });
      if (res.status === 401) return onLogout('Session expired');
      if (!res.ok) return showToast('Export failed', 'error');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'repatikosam-subscribers.csv';
      a.click();
      URL.revokeObjectURL(url);
      showToast('CSV downloaded');
    } catch {
      showToast('Export failed', 'error');
    }
  };

  const exportGstCsv = async () => {
    try {
      const res = await fetch(`${API}/api/admin/invoices/export`, { headers: adminHeaders(token) });
      if (res.status === 401) return onLogout('Session expired');
      if (!res.ok) return showToast('GST export failed', 'error');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'repatikosam-gst-invoices.csv';
      a.click();
      URL.revokeObjectURL(url);
      showToast('GST invoice register downloaded');
    } catch {
      showToast('GST export failed', 'error');
    }
  };

  const openInvoice = (invOrId, { download = false } = {}) => {
    const id = typeof invOrId === 'object' ? invOrId.id : invOrId;
    const invoiceNo =
      typeof invOrId === 'object' ? invOrId.invoiceNo || `invoice-${id}` : `invoice-${id}`;
    const url = `${API}/api/admin/invoices/${id}/html${download ? '?download=1' : ''}`;
    (async () => {
      try {
        if (!download) {
          setInvoiceModal({ id, invoiceNo, html: '', loading: true });
        }
        const viewUrl = download
          ? url
          : `${API}/api/admin/invoices/${id}/html?embed=1`;
        const res = await fetch(download ? url : viewUrl, { headers: adminHeaders(token) });
        if (res.status === 401) {
          setInvoiceModal(null);
          return onLogout('Session expired');
        }
        if (!res.ok) {
          setInvoiceModal(null);
          return showToast('Could not open invoice', 'error');
        }
        const html = await res.text();
        if (download) {
          const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
          const href = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = href;
          a.download = `${String(invoiceNo).replace(/[^\w.-]+/g, '_')}.html`;
          a.click();
          URL.revokeObjectURL(href);
          showToast('Invoice downloaded — open and Print / Save as PDF');
          return;
        }
        setInvoiceModal({ id, invoiceNo, html, loading: false });
      } catch {
        setInvoiceModal(null);
        showToast('Could not open invoice', 'error');
      }
    })();
  };

  const closeInvoiceModal = () => setInvoiceModal(null);

  const printInvoiceModal = () => {
    const frame = invoiceFrameRef.current;
    if (!frame?.contentWindow) return showToast('Bill still loading…', 'error');
    frame.contentWindow.focus();
    frame.contentWindow.print();
  };

  const downloadInvoiceModal = () => {
    if (!invoiceModal?.html) return;
    const blob = new Blob([invoiceModal.html], { type: 'text/html;charset=utf-8' });
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = `${String(invoiceModal.invoiceNo || `invoice-${invoiceModal.id}`).replace(/[^\w.-]+/g, '_')}.html`;
    a.click();
    URL.revokeObjectURL(href);
    showToast('Invoice downloaded — open and Print / Save as PDF');
  };

  useEffect(() => {
    if (!invoiceModal) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeInvoiceModal();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [invoiceModal]);

  const openBulkPrint = async () => {
    setBulkBusy(true);
    try {
      const res = await fetch(`${API}/api/admin/invoices/bulk.html`, { headers: adminHeaders(token) });
      if (res.status === 401) return onLogout('Session expired');
      if (!res.ok) return showToast('Could not open bulk bills', 'error');
      const html = await res.text();
      const w = window.open('', '_blank', 'noopener');
      if (!w) return showToast('Allow pop-ups to view bills', 'error');
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch {
      showToast('Could not open bulk bills', 'error');
    } finally {
      setBulkBusy(false);
    }
  };

  const downloadBulkZip = async () => {
    setBulkBusy(true);
    try {
      const res = await fetch(`${API}/api/admin/invoices/bulk.zip`, { headers: adminHeaders(token) });
      if (res.status === 401) return onLogout('Session expired');
      if (!res.ok) return showToast('Bulk download failed', 'error');
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = `repatikosam-gst-bills-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(href);
      showToast('ZIP downloaded — send to GST / CA (HTML bills + CSV register)');
    } catch {
      showToast('Bulk download failed', 'error');
    } finally {
      setBulkBusy(false);
    }
  };

  const grantYear = async (userId, email) => {
    if (!window.confirm(`Grant 1 year paid access to ${email || 'this user'}?`)) return;
    setGrantingId(userId);
    try {
      const res = await fetch(`${API}/api/admin/subscribers/${userId}/grant`, {
        method: 'POST',
        headers: { ...adminHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify({ days: 365 }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 401) return onLogout(adminAuthErrorMessage(res, json));
      if (!res.ok) showToast(json.error || 'Could not grant access', 'error');
      else {
        showToast(`Access granted to ${email || 'user'}`);
        load();
      }
    } catch {
      showToast('Network error granting access', 'error');
    }
    setGrantingId(null);
  };

  const revokeAccess = async (userId, email) => {
    if (
      !window.confirm(
        `Remove paid access for ${email || 'this user'}?\n\nThey will need to subscribe again to read the full paper.`
      )
    ) {
      return;
    }
    setRevokingId(userId);
    try {
      const res = await fetch(`${API}/api/admin/subscribers/${userId}/revoke`, {
        method: 'POST',
        headers: adminHeaders(token),
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 401) {
        setRevokingId(null);
        return onLogout(adminAuthErrorMessage(res, json));
      }
      if (!res.ok) showToast(json.error || 'Could not revoke access', 'error');
      else {
        showToast(`Access removed for ${email || 'user'}`);
        load();
      }
    } catch {
      showToast('Network error revoking access', 'error');
    }
    setRevokingId(null);
  };

  const grantByEmail = async () => {
    const email = grantEmail.trim();
    if (!email) return showToast('Enter a Gmail address', 'error');
    if (!window.confirm(`Activate 1 year paid access for ${email}?`)) return;
    setGrantingEmail(true);
    try {
      const res = await fetch(`${API}/api/admin/subscribers/grant-email`, {
        method: 'POST',
        headers: { ...adminHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, days: 365 }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 401) return onLogout(adminAuthErrorMessage(res, json));
      if (!res.ok) showToast(json.error || 'Could not activate', 'error');
      else {
        showToast(`Activated ${email} — ask them to sign out and back in`);
        setGrantEmail('');
        load();
      }
    } catch {
      showToast('Network error activating access', 'error');
    }
    setGrantingEmail(false);
  };

  const recoverByPaymentId = async () => {
    const paymentId = recoverPaymentId.trim();
    const email = grantEmail.trim();
    if (!paymentId) return showToast('Enter Razorpay payment id (pay_…)', 'error');
    if (!window.confirm(`Capture (if needed) and activate for ${paymentId}?`)) return;
    setRecoveringPayment(true);
    try {
      const res = await fetch(`${API}/api/admin/payments/recover`, {
        method: 'POST',
        headers: { ...adminHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentId, email: email || undefined }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 401) return onLogout(adminAuthErrorMessage(res, json));
      if (!res.ok) showToast(json.error || 'Could not recover payment', 'error');
      else {
        showToast(
          `Unlocked ${json.email || email || 'reader'}${json.invoiceNo ? ` · invoice ${json.invoiceNo}` : ''}`
        );
        setRecoverPaymentId('');
        load();
        loadInvoices();
      }
    } catch {
      showToast('Network error recovering payment', 'error');
    }
    setRecoveringPayment(false);
  };

  const reconcilePayments = async () => {
    if (!window.confirm('Find Razorpay-paid users who are still locked and unlock them?')) return;
    setReconciling(true);
    try {
      const res = await fetch(`${API}/api/admin/subscribers/reconcile-payments`, {
        method: 'POST',
        headers: adminHeaders(token),
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 401) return onLogout(adminAuthErrorMessage(res, json));
      if (!res.ok) showToast(json.error || 'Reconcile failed', 'error');
      else {
        showToast(json.message || `Fixed ${json.fixed ?? 0} account(s)`);
        load();
      }
    } catch {
      showToast('Network error reconciling', 'error');
    }
    setReconciling(false);
  };

  const syncMissingInvoices = async () => {
    if (
      !window.confirm(
        'Create GST invoices for paid subscribers who paid on Razorpay but have no bill yet?'
      )
    ) {
      return;
    }
    setSyncingInvoices(true);
    try {
      const res = await fetch(`${API}/api/admin/invoices/sync-missing`, {
        method: 'POST',
        headers: { ...adminHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 401) return onLogout(adminAuthErrorMessage(res, json));
      if (!res.ok) showToast(json.error || 'Invoice sync failed', 'error');
      else {
        const n = (json.invoicesCreated || 0) + (json.invoicesAssigned || 0);
        showToast(
          n
            ? `Invoices updated: ${json.invoicesCreated || 0} created, ${json.invoicesAssigned || 0} assigned`
            : 'No missing Razorpay invoices found (complimentary grants stay without bills)'
        );
        loadInvoices();
      }
    } catch {
      showToast('Network error syncing invoices', 'error');
    }
    setSyncingInvoices(false);
  };

  const q = search.trim().toLowerCase();

  const { paidUsers, freeUsers, otherUsers } = useMemo(() => {
    const paid = [];
    const free = [];
    const other = [];
    for (const u of data.users) {
      if (!matchesSearch(u, q)) continue;
      if (u.status === 'paid' || u.status === 'complimentary') paid.push(u);
      else if (u.status === 'free') free.push(u);
      else other.push(u);
    }
    return { paidUsers: paid, freeUsers: free, otherUsers: other };
  }, [data.users, q]);

  const visible = tab === 'paid' ? paidUsers : tab === 'free' ? freeUsers : otherUsers;

  const visibleBills = useMemo(() => {
    const bq = billSearch.trim().toLowerCase();
    if (!bq) return invoiceData.invoices;
    return invoiceData.invoices.filter((inv) => {
      const hay = `${inv.invoiceNo} ${inv.name || ''} ${inv.email || ''} ${inv.paymentId || ''} ${inv.orderId || ''}`.toLowerCase();
      return hay.includes(bq);
    });
  }, [invoiceData.invoices, billSearch]);

  return (
    <div className="adm-panel adm-subs">
      <div className="adm-panel__toolbar">
        <p className="adm-panel__note">
          Readers, unlock tools, and GST tax invoices for {BUSINESS.legalName} ({BUSINESS.gstin}).
        </p>
        <div className="adm-inline-actions">
          {view === 'readers' ? (
            <button type="button" className="adm-btn adm-btn-ghost" onClick={exportCsv}>
              Export readers CSV
            </button>
          ) : (
            <>
              <button type="button" className="adm-btn adm-btn-ghost" onClick={exportGstCsv}>
                Export GST CSV
              </button>
              <button
                type="button"
                className="adm-btn adm-btn-ghost"
                onClick={openBulkPrint}
                disabled={bulkBusy}
              >
                Print all bills
              </button>
              <button
                type="button"
                className="adm-btn adm-btn-primary"
                onClick={downloadBulkZip}
                disabled={bulkBusy}
              >
                {bulkBusy ? 'Preparing…' : 'Bulk download bills'}
              </button>
            </>
          )}
          <button
            type="button"
            className="adm-btn adm-btn-ghost"
            onClick={view === 'readers' ? load : loadInvoices}
            disabled={view === 'readers' ? loading : billsLoading}
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="adm-view-tabs" role="tablist" aria-label="Subscribers views">
        <button
          type="button"
          role="tab"
          aria-selected={view === 'readers'}
          className={`adm-view-tabs__btn ${view === 'readers' ? 'active' : ''}`}
          onClick={() => setView('readers')}
        >
          Readers
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === 'bills'}
          className={`adm-view-tabs__btn ${view === 'bills' ? 'active' : ''}`}
          onClick={() => setView('bills')}
        >
          GST Bills
        </button>
      </div>

      {view === 'bills' ? (
        <>
          <div className="adm-billing-hero">
            <div>
              <h3>{BUSINESS.legalName}</h3>
              <p>
                GSTIN {BUSINESS.gstin} · {BUSINESS.city}, {BUSINESS.state} · SAC {BUSINESS.sac}
              </p>
              <p className="adm-billing-hero__hint">
                Open any bill to print / save PDF for GST returns and income tax records.
              </p>
            </div>
            <div className="adm-billing-hero__bank">
              <span>Bank</span>
              <strong>{BUSINESS.bank.bankName}</strong>
              <em>{BUSINESS.bank.accountNumber} · {BUSINESS.bank.ifsc}</em>
            </div>
          </div>

          <div className="adm-kpi-grid adm-kpi-grid--4">
            <div className="adm-kpi adm-kpi--accent">
              <span className="adm-kpi__label">Paid invoices</span>
              <span className="adm-kpi__value">{invoiceData.stats?.count ?? '—'}</span>
              <span className="adm-kpi__sub">Tax invoices issued</span>
            </div>
            <div className="adm-kpi">
              <span className="adm-kpi__label">Taxable</span>
              <span className="adm-kpi__value">{formatMoney(invoiceData.stats?.taxablePaise)}</span>
              <span className="adm-kpi__sub">Excl. GST</span>
            </div>
            <div className="adm-kpi">
              <span className="adm-kpi__label">CGST + SGST</span>
              <span className="adm-kpi__value">
                {formatMoney(
                  Number(invoiceData.stats?.cgstPaise || 0) + Number(invoiceData.stats?.sgstPaise || 0)
                )}
              </span>
              <span className="adm-kpi__sub">9% + 9%</span>
            </div>
            <div className="adm-kpi">
              <span className="adm-kpi__label">Gross collected</span>
              <span className="adm-kpi__value">{formatMoney(invoiceData.stats?.totalPaise)}</span>
              <span className="adm-kpi__sub">Incl. GST</span>
            </div>
          </div>

          <section className="adm-panel__body">
            <header className="adm-panel__section-head">
              <div>
                <h3>Paid tax invoices</h3>
                <p>
                  Same GST tax invoice the customer gets after payment. Use <strong>View / print</strong> for one bill,
                  or <strong>Bulk download bills</strong> for a ZIP to send your CA.
                  If someone became Paid after a late Razorpay success but has no bill, use{' '}
                  <strong>Sync missing invoices</strong>.
                </p>
              </div>
              <button
                type="button"
                className="adm-btn adm-btn-ghost"
                disabled={syncingInvoices}
                onClick={syncMissingInvoices}
              >
                {syncingInvoices ? 'Syncing…' : 'Sync missing invoices'}
              </button>
            </header>

            <div className="adm-panel__filters">
              <input
                type="search"
                className="adm-panel__search"
                value={billSearch}
                onChange={(e) => setBillSearch(e.target.value)}
                placeholder="Search invoice no, name, email, payment ID…"
              />
            </div>

            {billsLoading ? (
              <div className="adm-empty">Loading GST bills…</div>
            ) : visibleBills.length === 0 ? (
              <div className="adm-empty">No paid invoices yet.</div>
            ) : (
              <div className="adm-bill-table-wrap">
                <table className="adm-bill-table">
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      <th>Date</th>
                      <th>Customer</th>
                      <th>Taxable</th>
                      <th>CGST</th>
                      <th>SGST</th>
                      <th>Total</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {visibleBills.map((inv) => (
                      <tr key={inv.id}>
                        <td>
                          <code className="adm-bill-no">{inv.invoiceNo}</code>
                        </td>
                        <td>{inv.date}</td>
                        <td>
                          <div className="adm-bill-customer">
                            <strong>{inv.name || '—'}</strong>
                            <span>{inv.email || '—'}</span>
                          </div>
                        </td>
                        <td>{inv.baseInr}</td>
                        <td>{inv.cgstInr}</td>
                        <td>{inv.sgstInr}</td>
                        <td>
                          <strong>{inv.amountInr}</strong>
                        </td>
                        <td>
                          <div className="adm-bill-actions">
                            <button
                              type="button"
                              className="adm-btn adm-btn-primary adm-btn-sm"
                              onClick={() => openInvoice(inv)}
                            >
                              View / print
                            </button>
                            <button
                              type="button"
                              className="adm-btn adm-btn-ghost adm-btn-sm"
                              onClick={() => openInvoice(inv, { download: true })}
                            >
                              Download
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      ) : (
        <>
          <div className="adm-kpi-grid adm-kpi-grid--4">
            <div className="adm-kpi adm-kpi--accent">
              <span className="adm-kpi__label">Paid</span>
              <span className="adm-kpi__value">{data.stats?.paid ?? '—'}</span>
              <span className="adm-kpi__sub">Active subscribers</span>
            </div>
            <div className="adm-kpi">
              <span className="adm-kpi__label">Free</span>
              <span className="adm-kpi__value">{data.stats?.free ?? '—'}</span>
              <span className="adm-kpi__sub">Signed in, not paid</span>
            </div>
            <div className="adm-kpi">
              <span className="adm-kpi__label">WhatsApp ready</span>
              <span className="adm-kpi__value">{data.stats?.withWhatsApp ?? '—'}</span>
              <span className="adm-kpi__sub">Paid + number opted in</span>
            </div>
            <div className={`adm-kpi ${whatsappConfigured ? 'adm-kpi--ok' : 'adm-kpi--warn'}`}>
              <span className="adm-kpi__label">API</span>
              <span className="adm-kpi__value">{whatsappConfigured ? 'ON' : 'OFF'}</span>
              <span className="adm-kpi__sub">Cloud WhatsApp</span>
            </div>
          </div>

          <section className="adm-panel__body adm-panel__body--accent">
            <header className="adm-panel__section-head">
              <div>
                <h3>Quick unlock</h3>
                <p>
                  If Razorpay took money but the paper is still locked, activate by Google email
                  or recover with the Razorpay payment id (pay_…). Ask them to sign out and sign in again.
                </p>
              </div>
            </header>
            <div className="adm-unlock-row">
              <input
                type="email"
                value={grantEmail}
                onChange={(e) => setGrantEmail(e.target.value)}
                placeholder="reader@gmail.com"
                aria-label="Reader email"
              />
              <button
                type="button"
                className="adm-btn adm-btn-primary"
                disabled={grantingEmail}
                onClick={grantByEmail}
              >
                {grantingEmail ? 'Activating…' : 'Activate 1 year'}
              </button>
              <button
                type="button"
                className="adm-btn adm-btn-ghost"
                onClick={reconcilePayments}
                disabled={reconciling}
              >
                {reconciling ? 'Fixing…' : 'Fix all paid-but-locked'}
              </button>
            </div>
            <div className="adm-unlock-row" style={{ marginTop: 10 }}>
              <input
                type="text"
                value={recoverPaymentId}
                onChange={(e) => setRecoverPaymentId(e.target.value)}
                placeholder="pay_TS0doZ4WlxHFY2"
                aria-label="Razorpay payment id"
              />
              <button
                type="button"
                className="adm-btn adm-btn-primary"
                disabled={recoveringPayment}
                onClick={recoverByPaymentId}
              >
                {recoveringPayment ? 'Recovering…' : 'Capture + unlock'}
              </button>
            </div>
          </section>

          <section className="adm-panel__body">
            <header className="adm-panel__section-head">
              <div>
                <h3>Readers</h3>
                <p>
                  Filter by status, update mobile / WhatsApp, grant access, or revoke mistaken unlocks.
                  {!whatsappConfigured && ' WhatsApp Cloud API is not configured on the server yet.'}
                </p>
              </div>
            </header>

            <div className="adm-panel__filters">
              <input
                type="search"
                className="adm-panel__search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, or mobile…"
              />
              <div className="adm-seg" role="tablist" aria-label="Reader status">
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === 'paid'}
                  className={`adm-seg__btn ${tab === 'paid' ? 'active' : ''}`}
                  onClick={() => setTab('paid')}
                >
                  Paid <em>{paidUsers.length}</em>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === 'free'}
                  className={`adm-seg__btn ${tab === 'free' ? 'active' : ''}`}
                  onClick={() => setTab('free')}
                >
                  Free <em>{freeUsers.length}</em>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === 'other'}
                  className={`adm-seg__btn ${tab === 'other' ? 'active' : ''}`}
                  onClick={() => setTab('other')}
                >
                  Trial / Expired <em>{otherUsers.length}</em>
                </button>
              </div>
            </div>

            {loading ? (
              <div className="adm-empty">Loading readers…</div>
            ) : visible.length === 0 ? (
              <div className="adm-empty">No readers in this list.</div>
            ) : (
              <ul className="adm-person-list">
                {visible.map((u) => {
                  const row = editPhone[u.id] || { phone: '', optIn: true };
                  const waOn = !!u.phone && !!u.whatsapp_opt_in;
                  return (
                    <li key={u.id} className={`adm-person adm-person--${u.status}`}>
                      <div className="adm-person__top">
                        <div className="adm-person__who">
                          <span className="adm-person__avatar" aria-hidden>
                            {initials(u.name, u.email)}
                          </span>
                          <div className="adm-person__who-text">
                            <div className="adm-person__name">{u.name || 'Unnamed reader'}</div>
                            <div className="adm-person__email">{u.email || '—'}</div>
                          </div>
                        </div>
                        <span className={`adm-status adm-status--${u.status}`}>
                          {STATUS_LABEL[u.status] || u.status}
                        </span>
                      </div>

                      <div className="adm-person__facts">
                        <div>
                          <span>Paid until</span>
                          <strong>{formatDate(u.subscribed_until)}</strong>
                        </div>
                        <div>
                          <span>Last login</span>
                          <strong title={formatDateTime(u.last_login_at || 0)}>
                            {formatDateTime(u.last_login_at || 0)}
                          </strong>
                        </div>
                        <div>
                          <span>Joined</span>
                          <strong>{formatJoined(u.created_at)}</strong>
                        </div>
                        <div>
                          <span>WhatsApp</span>
                          <strong className={waOn ? 'is-on' : ''}>{waOn ? 'Ready' : 'Off'}</strong>
                        </div>
                      </div>

                      <div className="adm-person__contact">
                        <div className="adm-person__mobile">
                          <span className="adm-person__mobile-label">Mobile</span>
                          {editingPhoneId === u.id ? (
                            <div className="adm-person__mobile-edit">
                              <input
                                type="tel"
                                inputMode="numeric"
                                autoFocus
                                placeholder="10-digit mobile number"
                                value={row.phone}
                                onChange={(e) =>
                                  setEditPhone((prev) => ({
                                    ...prev,
                                    [u.id]: { ...row, phone: e.target.value },
                                  }))
                                }
                                aria-label={`Edit mobile for ${u.email || u.name || 'reader'}`}
                              />
                              <label className="adm-check">
                                <input
                                  type="checkbox"
                                  checked={!!row.optIn}
                                  onChange={(e) =>
                                    setEditPhone((prev) => ({
                                      ...prev,
                                      [u.id]: { ...row, optIn: e.target.checked },
                                    }))
                                  }
                                />
                                Alerts
                              </label>
                              <button
                                type="button"
                                className="adm-btn adm-btn-primary adm-btn-sm"
                                disabled={savingId === u.id}
                                onClick={() => savePhone(u.id)}
                              >
                                {savingId === u.id ? '…' : 'Save'}
                              </button>
                              <button
                                type="button"
                                className="adm-btn adm-btn-ghost adm-btn-sm"
                                disabled={savingId === u.id}
                                onClick={() => cancelEditPhone(u)}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="adm-person__mobile-view">
                              <div
                                className={`adm-person__mobile-box${u.phone ? '' : ' is-empty'}`}
                              >
                                {u.phone ? (
                                  <>
                                    <strong>{formatMobileDisplay(u.phone)}</strong>
                                    {!!u.whatsapp_opt_in && (
                                      <em className="adm-person__mobile-alerts">Alerts on</em>
                                    )}
                                  </>
                                ) : (
                                  <span>No mobile number</span>
                                )}
                              </div>
                              <button
                                type="button"
                                className="adm-btn adm-btn-ghost adm-btn-sm"
                                onClick={() => startEditPhone(u)}
                              >
                                {u.phone ? 'Edit' : 'Add number'}
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="adm-person__actions">
                          {u.status === 'paid' ||
                          u.status === 'complimentary' ||
                          u.status === 'trial' ? (
                            <>
                              <span
                                className={`adm-person__active${
                                  u.status === 'complimentary' ? ' is-comp' : ''
                                }`}
                              >
                                {u.status === 'trial'
                                  ? 'Trial'
                                  : u.status === 'complimentary'
                                    ? 'No payment'
                                    : 'Active'}
                              </span>
                              <button
                                type="button"
                                className="adm-btn adm-btn-ghost adm-btn-sm adm-person__revoke"
                                disabled={revokingId === u.id}
                                onClick={() => revokeAccess(u.id, u.email)}
                                title="Remove paid access — user must subscribe again"
                              >
                                {revokingId === u.id ? '…' : 'Revoke'}
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className="adm-btn adm-btn-primary adm-btn-sm"
                              disabled={grantingId === u.id}
                              onClick={() => grantYear(u.id, u.email)}
                            >
                              {grantingId === u.id ? '…' : 'Grant 1 year'}
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </>
      )}

      {invoiceModal && (
        <div
          className="adm-invoice-overlay"
          role="presentation"
          onClick={closeInvoiceModal}
        >
          <div
            className="adm-invoice-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Tax invoice ${invoiceModal.invoiceNo || ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <header className="adm-invoice-modal__head">
              <div>
                <h3>Tax invoice</h3>
                <p>{invoiceModal.invoiceNo || 'Loading…'}</p>
              </div>
              <div className="adm-invoice-modal__actions">
                <button
                  type="button"
                  className="adm-btn adm-btn-ghost adm-btn-sm"
                  disabled={invoiceModal.loading || !invoiceModal.html}
                  onClick={downloadInvoiceModal}
                >
                  Download
                </button>
                <button
                  type="button"
                  className="adm-btn adm-btn-primary adm-btn-sm"
                  disabled={invoiceModal.loading || !invoiceModal.html}
                  onClick={printInvoiceModal}
                >
                  Print / Save PDF
                </button>
                <button
                  type="button"
                  className="adm-btn adm-btn-ghost adm-btn-sm"
                  onClick={closeInvoiceModal}
                  aria-label="Close"
                >
                  Close
                </button>
              </div>
            </header>
            <div className="adm-invoice-modal__body">
              {invoiceModal.loading ? (
                <div className="adm-empty">Loading bill…</div>
              ) : (
                <iframe
                  ref={invoiceFrameRef}
                  className="adm-invoice-modal__frame"
                  title={`Invoice ${invoiceModal.invoiceNo || invoiceModal.id}`}
                  srcDoc={invoiceModal.html}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
